import {ILcTransitionParamsProvider} from "/@lib@/wsp/wspMetaUi.js";
import {JSrcFields} from "/@lib@/wsp/src.js";
import {IReg} from "/@lib@/commons/registry.js";
import {IWspUiEnv} from "/@lib@/wsp/wsp.js";
import {CidExchangeStep, CidProcess, CidSession, CidUploadStep, ECidSessionState, ICidWebUploadDef, OCidSessionConfig, parseCidStepUrl} from "/@lib@/core/cid.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {CidInteractionByStepsUi, OCidInteractionByStepsUiInit} from "/@back@/core/widgets/cidUi.js";
import {LANG} from "/@lib@/commons/lang.js";

/**
 * Fournisseur de params CID
 */
export class CidLcTransitionParamsProvider implements ILcTransitionParamsProvider {
	constructor(protected readonly config: OCidLcTransitionParamsProviderConfig) {}

	async computeParams(params: { [refUri: string]: { [key: string]: Jsonisable } }, shortDescs: JSrcFields[], reg: IReg<IWspUiEnv>, emitter: HTMLElement): Promise<boolean> {
		const ct = <CidInteractionByStepsUi/>;

		ct.initialize({
			reg: reg,
			skinOver: "webzone:panel",
			cidProcessIs: Array.isArray(this.config.cidProcessFilter) ? this.config.cidProcessFilter : this.config.cidProcessFilter({reg: reg}),
			cidHostIs: this.config.cidHostIs,
			cidManifestUrlInput: this.config.cidManifestUrlInput,
			cidPassOptionalInteraction: ("cidPassOptionalInteraction" in this.config) ? this.config.cidPassOptionalInteraction : true,
			cidHostUse: this.config.cidHostUse,
			cidSessionConfig: {
				customUpload: async (session: CidSession, uploadStep: CidUploadStep, webUpload: ICidWebUploadDef): Promise<null | Response> => {
					session.sessionProps.uploadUrl = uploadStep.uploadUrl;
					session.sessionProps.uploadUrlMethod = 'PUT';
					// Upload reporté dans les steps du LC
					return new Response(null, {status: 200});
				}
			} as OCidSessionConfig,
			onInteractionEndHandler: async (cidSession: CidSession) => {
				switch (cidSession.state) {
					case ECidSessionState.failed:
						await ERROR.showError({
							msg: "Échec d'interaction avec le serveur de ressources",
							details: cidSession.failedReason
						});
						break;
					case ECidSessionState.ended:
						if (!params['*'])
							params['*'] = {};
						Object.assign(params['*'], cidSession.metasOut);
						//params['*'].scCidSessId = cidSession.sessionProps.scCidSessId;
						params['*'].uploadUrl = cidSession.sessionProps.uploadUrl;
						params['*'].uploadUrlMethod = cidSession.sessionProps.uploadUrlMethod;

						const cidInteractStep: CidUploadStep = cidSession.cidProcess.steps[1] instanceof CidUploadStep ? cidSession.cidProcess.steps[1] : null;
						const systemWaitDef = cidInteractStep?.getSystemWaitDef();
						const systemWaitUrlObj = systemWaitDef ? parseCidStepUrl(systemWaitDef) : null;
						const systemWaitUrl = systemWaitUrlObj ? systemWaitUrlObj.getStr("fr") : null;
						if (systemWaitUrl) params['*'].systemWaitUrl = systemWaitUrl;

						// Recherche des "scCidSessId" : une session cid par item pusché
						// FIXME : traitement par lot cid ?
						const cidExchangeStep: CidExchangeStep = cidSession.cidProcess.steps[0] instanceof CidExchangeStep ? cidSession.cidProcess.steps[0] : null;
						await LANG.promisesMap(shortDescs, async (shortDesc, i) => {
							if (i != 0) await cidExchangeStep?.doStep(cidSession);
							if (!params[shortDesc.srcUri])
								params[shortDesc.srcUri] = {};
							params[shortDesc.srcUri].scCidSessId = cidSession.sessionProps.scCidSessId;
						}, 10);
						popup.close(true);
				}
				popup.close(false);
			}
		} as OCidInteractionByStepsUiInit);

		const popup = POPUP.showDialog(ct, reg.env.uiRoot, {
			titleBar: {barLabel: {label: this.config.label || "Serveur de ressources..."}, closeButton: {}},
			initWidth: '70vw',
			initHeight: '70vh',
			resizer: {}
		});

		return await popup.onNextClose() as boolean;
	}

	async rollbackTransition(params: { [refUri: string]: { [key: string]: Jsonisable } }, shortDescs: JSrcFields[], ) {
		const abortUrl = params['*']?.systemWaitUrl;
		if (!abortUrl) await ERROR.logError({ msg: "Unable to abort the transition CID sessions: no systemWaitUrl available"});
		const abortUrlObj = new URL(abortUrl);
		abortUrlObj.searchParams.set("scResp", "200-202-403");
		abortUrlObj.searchParams.set("abortSession", "");
		await LANG.promisesMap(shortDescs, async (shortDesc) => {
			const scCidSessId = params[shortDesc.srcUri]?.scCidSessId;
			abortUrlObj.searchParams.set("scCidSessId", scCidSessId);
			return fetch(abortUrlObj.href, {method: "POST", credentials: "include"});
		}, 10);
	}
}

export interface OCidLcTransitionParamsProviderConfig {

	label?: string

	cidProcessFilter: string[] | string[][] | ((ctx: { reg: IReg<IWspUiEnv> }) => CidProcess[]);

	cidHostIs?: string[] | string[][];

	cidManifestUrlInput?: boolean;

	cidPassOptionalInteraction?: boolean;

	cidHostUse?: 'input' | 'output' | 'both';
}